/**
 * 视频压缩工具
 * 基于 Canvas + MediaRecorder 方案，将视频缩放并重新录制为 WebM
 */

/** 默认最大边长（px），超过此值的视频会等比缩放 */
const DEFAULT_MAX_EDGE = 480;
/** 默认视频码率（bps） */
const DEFAULT_BITRATE = 800_000; // 0.8 Mbps
/** 小于此大小（字节）的视频直接跳过压缩 */
const SKIP_COMPRESS_THRESHOLD = 1 * 1024 * 1024; // 1MB
/** 最大压缩时长（毫秒），超过则截断 */
const MAX_COMPRESS_DURATION = 30_000; // 30秒

/**
 * 压缩视频文件
 *
 * @param file      原始视频 File 对象
 * @param maxEdge   最大边长（px），默认 720
 * @param bitrate   输出视频码率，默认 1.5Mbps
 * @returns 压缩后的 File 对象；若无需压缩或失败则返回原文件
 */
export async function compressVideo(
  file: File,
  maxEdge: number = DEFAULT_MAX_EDGE,
  bitrate: number = DEFAULT_BITRATE,
): Promise<File> {
  // 非视频类型直接返回
  if (!file.type.startsWith('video/')) {
    return file;
  }

  // 小文件跳过压缩
  if (file.size <= SKIP_COMPRESS_THRESHOLD) {
    console.log(`[videoCompress] 文件 ${file.name} 大小 ${(file.size / 1024 / 1024).toFixed(2)}MB，低于阈值跳过压缩`);
    return file;
  }

  // 检查 MediaRecorder 支持
  const mimeType = pickSupportedMimeType();
  if (!mimeType) {
    console.warn('[videoCompress] 浏览器不支持 MediaRecorder，跳过压缩');
    return file;
  }

  try {
    const video = await loadVideoMetadata(file);
    const { videoWidth: srcW, videoHeight: srcH, duration } = video;

    // 计算缩放后的尺寸
    const { width, height } = calcScaledSize(srcW, srcH, maxEdge);

    const compressDuration = Math.min(duration, MAX_COMPRESS_DURATION / 1000);

    const blob = await recordVideoViaCanvas(video, width, height, compressDuration, bitrate, mimeType);
    // 释放视频资源
    video.pause();
    video.src = '';
    video.load();

    if (!blob) {
      console.warn('[videoCompress] 录制失败，降级使用原视频');
      return file;
    }

    // 生成压缩后的 File
    const baseName = file.name.replace(/\.[^.]+$/, '');
    const compressedFile = new File([blob], `${baseName}.webm`, { type: 'video/webm' });

    const originalMB = (file.size / 1024 / 1024).toFixed(2);
    const compressedMB = (compressedFile.size / 1024 / 1024).toFixed(2);
    const ratio = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
    console.log(`[videoCompress] ${file.name} 压缩完成: ${originalMB}MB -> ${compressedMB}MB (压缩率 ${ratio}%), 尺寸 ${srcW}x${srcH} -> ${width}x${height}`);

    return compressedFile;
  } catch (err) {
    console.warn('[videoCompress] 压缩失败，降级使用原视频:', err);
    return file;
  }
}

/**
 * 加载视频并等待元数据就绪
 */
function loadVideoMetadata(file: File): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.src = url;

    const onLoaded = () => {
      // 确保视频可以播放
      video.currentTime = 0;
      URL.revokeObjectURL(url);
      resolve(video);
    };
    const onError = () => {
      URL.revokeObjectURL(url);
      reject(new Error('视频加载失败'));
    };

    video.onloadeddata = onLoaded;
    video.onerror = onError;
    // 超时保护（10秒）
    setTimeout(() => {
      if (video.readyState < 2) {
        URL.revokeObjectURL(url);
        reject(new Error('视频加载超时'));
      }
    }, 10_000);
  });
}

/**
 * 通过 Canvas 绘制视频帧并用 MediaRecorder 录制
 */
function recordVideoViaCanvas(
  video: HTMLVideoElement,
  width: number,
  height: number,
  duration: number,
  bitrate: number,
  mimeType: string,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      resolve(null);
      return;
    }

    // 检查 captureStream 支持
    const stream = (canvas as HTMLCanvasElement & {
      captureStream?: (frameRate?: number) => MediaStream;
    }).captureStream?.(30);
    if (!stream) {
      console.warn('[videoCompress] canvas.captureStream 不支持');
      resolve(null);
      return;
    }

    const recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: bitrate,
    });

    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      resolve(blob.size > 0 ? blob : null);
    };
    recorder.onerror = () => {
      resolve(null);
    };

    let rafId: number | null = null;
    let startTime = 0;

    const drawFrame = () => {
      if (video.paused || video.ended) {
        if (recorder.state !== 'inactive') recorder.stop();
        return;
      }
      ctx.drawImage(video, 0, 0, width, height);
      rafId = requestAnimationFrame(drawFrame);
    };

    const cleanup = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      video.pause();
      if (recorder.state !== 'inactive') recorder.stop();
    };

    // 超时保护
    const timeoutId = setTimeout(() => {
      cleanup();
    }, MAX_COMPRESS_DURATION);

    recorder.start();
    startTime = performance.now();

    video.currentTime = 0;
    video.muted = true;
    video.play().then(() => {
      drawFrame();
    }).catch(() => {
      cleanup();
      clearTimeout(timeoutId);
      resolve(null);
    });

    // 视频播放结束或达到压缩时长上限时停止
    const checkEnd = () => {
      const elapsed = (performance.now() - startTime) / 1000;
      if (video.ended || elapsed >= duration) {
        cleanup();
        clearTimeout(timeoutId);
      } else {
        requestAnimationFrame(checkEnd);
      }
    };
    requestAnimationFrame(checkEnd);
  });
}

/**
 * 等比计算缩放后的尺寸
 */
function calcScaledSize(srcW: number, srcH: number, maxEdge: number): { width: number; height: number } {
  const maxSrc = Math.max(srcW, srcH);
  if (maxSrc <= maxEdge) {
    return { width: srcW, height: srcH };
  }
  const scale = maxEdge / maxSrc;
  return {
    width: Math.round(srcW * scale),
    height: Math.round(srcH * scale),
  };
}

/**
 * 选择浏览器支持的录制 MIME 类型
 */
function pickSupportedMimeType(): string | null {
  const candidates = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return null;
}
